/**
 * Gallery scene: quiet toast chrome (?test=maater-ui#toasts)
 *
 * Art until Rowan locks. Static mocks show before / after; buttons fire real
 * Sonner toasts through `quietToastOptions` so gestures can be felt.
 */
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import {
  AlertCircle,
  ArrowDownToLine,
  CheckCircle2,
  Info,
  Loader2,
  RefreshCw,
  X,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Toaster } from '@/components/ui/toaster'
import { quietToastOptions, QUIET_TOAST_DURATION_MS } from '@/lib/toast-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type MockTone = 'success' | 'info' | 'error' | 'loading'

const TONE_ICON: Record<MockTone, { icon: LucideIcon; className: string }> = {
  success: { icon: CheckCircle2, className: 'text-green-600 dark:text-green-500' },
  info: { icon: Info, className: 'text-primary' },
  error: { icon: AlertCircle, className: 'text-destructive' },
  loading: { icon: Loader2, className: 'animate-spin text-muted-foreground' },
}

function QuietToastMock({
  tone,
  title,
  description,
  action,
  showClose = false,
}: {
  tone: MockTone
  title: string
  description?: string
  action?: string
  showClose?: boolean
}) {
  const { icon: Icon, className } = TONE_ICON[tone]
  return (
    <div
      className="group relative flex w-fit max-w-[24rem] items-center gap-2.5 rounded-[10px] border border-border bg-popover py-2 pl-3 pr-2.5 text-popover-foreground shadow-[0_1px_2px_hsl(0_0%_0%/0.06),0_6px_20px_-6px_hsl(0_0%_0%/0.18)]"
    >
      <Icon className={cn('h-4 w-4 shrink-0', className)} />
      <div className="min-w-0">
        <p className="text-[13px] font-medium leading-snug text-foreground">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      {action && (
        <span className="ml-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10">
          {action}
        </span>
      )}
      <span
        className={cn(
          'absolute right-0 top-0 flex h-[18px] w-[18px] -translate-y-[35%] translate-x-[35%] items-center justify-center rounded-full border border-border bg-popover text-muted-foreground transition-opacity',
          showClose ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
      >
        <X className="h-2.5 w-2.5" />
      </span>
    </div>
  )
}

function LoudToastMock() {
  return (
    <div className="flex min-w-[400px] max-w-[540px] items-center gap-3 rounded-md border-2 border-green-600 bg-green-600/95 px-4 py-3 text-white shadow-xl">
      <CheckCircle2 className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Changes applied successfully</p>
        <p className="text-sm text-white/90">test todo</p>
      </div>
      <span className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
        Undo
      </span>
    </div>
  )
}

function Label({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  )
}

function fireQuietSuccess() {
  toast.success('Saved', quietToastOptions('success'))
}

function fireQuietUndo() {
  toast.success('Applied to test todo', quietToastOptions('success', {
    action: {
      label: 'Undo',
      onClick: () => toast('Undone', quietToastOptions('success')),
    },
  }))
}

function fireQuietUpdate() {
  toast.info('Update ready', quietToastOptions('info', {
    id: 'maater-ui-gallery-update',
    description: 'Reload to get the latest Maater.',
    duration: Infinity,
    action: { label: 'Reload', onClick: () => {} },
  }))
}

function fireQuietError() {
  toast.error('Could not apply edit', quietToastOptions('error', {
    description: 'Document changed since the proposal. Try again.',
  }))
}

function fireQuietPromise() {
  const id = toast.loading('Importing 3 images...', quietToastOptions('info', { duration: Infinity }))
  window.setTimeout(() => {
    toast.success('Imported 3 images', quietToastOptions('success', { id }))
  }, 1600)
}

function fireLegacySuccess() {
  toast.success('Changes applied successfully', {
    description: 'test todo',
    duration: 6000,
    action: { label: 'Undo', onClick: () => {} },
  })
}

const GESTURES: { input: string; result: string }[] = [
  { input: 'Hover', result: 'Pauses the timer and reveals the corner close chip' },
  { input: 'Swipe down (or left / right)', result: 'Dismiss. Sonner swipe - bottom-center defaults to down; rollout adds left / right' },
  { input: 'Click X', result: 'Dismiss now (keyboard: Tab to it; focus-within shows it)' },
  { input: 'Alt+T', result: 'Focus the toast stack (Sonner hotkey) - then Tab to action / close' },
  { input: 'Click action', result: 'Runs Undo / Reload and dismisses' },
  { input: 'Several at once', result: 'Collapsed stack (expand=false); hover fans them out' },
]

const SUGGESTIONS: string[] = [
  'Tone lives on a 16px icon only. No solid green / blue / red fills in either theme.',
  'Title says what happened in 2-4 words ("Applied to test todo"); the doc name goes in the title, not a second line.',
  'Success without an action: 3s. With Undo: 8s (never 30s). Hover pauses, so short is safe.',
  'Plain success with no action is a smell - prefer inline confirmation (tab dirty dot clears, status bar "Saved"). Toast only when the result is off-screen.',
  'Action is a quiet rust text button, not a filled primary button.',
  'Every toast can be dismissed: hover X + swipe. Persistent toasts (update ready) must have X.',
  '"App updated" should become a status-bar / rail pip ("Update ready - Reload") so it never sits over content. Toast fallback shown here.',
  'Loading -> settled uses one toast id (promise pattern) so the frame morphs instead of stacking two toasts.',
  'Width hugs content (max 24rem) instead of min 400px; one hairline border instead of border-2 + shadow-xl.',
]

export function ToastChromeScene() {
  return (
    <div className="space-y-6" data-testid={testIds.maaterUiGallery.toastChrome}>
      {/* The ?test= route mounts outside App's Toaster. Icons and chrome
          come from the production Toaster. */}
      <Toaster />
      <p className="text-sm text-muted-foreground">
        <strong className="font-medium text-foreground">Toasts</strong> - quiet
        chrome: neutral popover card, tone on the icon, compact, dismissible.
        Production <code className="text-xs">Toaster</code> uses this. Timed copy
        goes through <code className="text-xs">quietToastOptions</code> from{' '}
        <code className="text-xs">@/lib/toast-chrome</code>.
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Today (loud)</Label>
          <LoudToastMock />
        </div>
        <div className="space-y-2">
          <Label>Proposed (quiet) - hover for close</Label>
          <QuietToastMock tone="success" title="Applied to test todo" action="Undo" />
        </div>
      </div>

      <div className="space-y-3">
        <Label>Variants</Label>
        <div className="flex flex-wrap items-start gap-4">
          <QuietToastMock tone="success" title="Saved" />
          <QuietToastMock tone="success" title="Applied to test todo" action="Undo" showClose />
          <QuietToastMock
            tone="info"
            title="Update ready"
            description="Reload to get the latest Maater."
            action="Reload"
            showClose
          />
          <QuietToastMock
            tone="error"
            title="Could not apply edit"
            description="Document changed since the proposal. Try again."
            showClose
          />
          <QuietToastMock tone="loading" title="Importing 3 images..." />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Update ready - preferred home (status bar pip, not a toast)</Label>
        <div className="flex h-6 w-full max-w-md items-center justify-between rounded-md border border-border bg-muted/30 px-2 text-[11px] text-muted-foreground">
          <span>12 documents</span>
          <button
            type="button"
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-primary hover:bg-primary/10"
          >
            <ArrowDownToLine className="h-3 w-3" />
            Update ready - Reload
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Try it (real Sonner toasts)</Label>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={fireQuietSuccess}>
            Success
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={fireQuietUndo}
            data-testid={testIds.maaterUiGallery.toastChromeFireUndo}
          >
            Success + Undo
          </Button>
          <Button size="sm" variant="outline" onClick={fireQuietUpdate}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Update ready
          </Button>
          <Button size="sm" variant="outline" onClick={fireQuietError}>
            Error
          </Button>
          <Button size="sm" variant="outline" onClick={fireQuietPromise}>
            Loading to done
          </Button>
          <Button size="sm" variant="ghost" onClick={fireLegacySuccess}>
            Compare: today&apos;s loud toast
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Durations: success {QUIET_TOAST_DURATION_MS.success / 1000}s, with action{' '}
          {QUIET_TOAST_DURATION_MS.withAction / 1000}s, info{' '}
          {QUIET_TOAST_DURATION_MS.info / 1000}s, error {QUIET_TOAST_DURATION_MS.error / 1000}s.
          Hover pauses.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Gestures</Label>
          <table className="w-full text-xs">
            <tbody>
              {GESTURES.map((g) => (
                <tr key={g.input} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-3 font-medium text-foreground">{g.input}</td>
                  <td className="py-1.5 text-muted-foreground">{g.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-2">
          <Label>Suggestions for rollout</Label>
          <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
            {SUGGESTIONS.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
